import { FC, useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Stack, TextField, Typography } from '@mui/material'

import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { WysiwygEditorConfig } from '../../../../components/InputTranslator/InputTranslatorDialog/InputTranslatorDialog'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { InternalImageUpload } from '../../../../components/InternalImageUpload/InternalImageUpload'
import { InputTranslatorType } from '../../../../types/InputTranslatorType'
import { categoryLanguages } from '../../helpers/categoryLanguageHelpers'
import { maxIconBytes } from '../helpers/categoryHelpers'
import { ExampleItemValues } from '../helpers/featureHelpers'

export const exampleEditorConfig: WysiwygEditorConfig = {
  plugins: ['link', 'lists', 'code', 'autoresize'],
  toolbar: 'undo redo | blocks | bold italic underline | bullist numlist | outdent indent | link | code removeformat',
}

type ExampleItemDialogProps = {
  open: boolean
  item?: ExampleItemValues
  onClose: () => void
  onSave: (item: ExampleItemValues) => void
}

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const ExampleItemDialog: FC<ExampleItemDialogProps> = ({ open, item, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useForm<ExampleItemValues>({ mode: 'onChange', defaultValues: item })
  const english = useWatch({ control, name: 'titles' })?.en

  useEffect(() => {
    if (open && item) reset(item)
  }, [open, item, reset])

  const submit = handleSubmit((values) => onSave(values))

  return (
    <InternalFormDialog
      open={open}
      title={t('internal-analysts:categories_example_item_edit')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!english?.trim()}
      maxWidth="md"
    >
      <Stack spacing={2} pt={1}>
        <Stack spacing={1}>
          <Typography variant="body2" fontWeight="bold">
            {t('internal-analysts:categories_example_image')}
          </Typography>
          <Controller
            name="image"
            control={control}
            render={({ field }) => (
              <InternalImageUpload
                value={field.value?.url ?? ''}
                onChange={(url, file) => field.onChange(url && file ? { id: file.id, url: file.url } : null)}
                type="examples"
                label={t('internal-analysts:categories_example_image')}
                removable
                previewVariant="contain"
                maxBytes={maxIconBytes}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary">
            {t('internal-analysts:categories_example_image_help')}
          </Typography>
        </Stack>
        <Controller
          name="imageCopyright"
          control={control}
          render={({ field }) => <TextField {...field} size="small" label={t('internal-analysts:categories_example_image_copyright')} />}
        />
        <Controller
          name="titles"
          control={control}
          rules={{ validate: (titles) => !!titles.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-analysts:categories_example_item_title')}
              dialogTitle={t('internal-analysts:categories_example_item_title')}
              selectedLanguages={categoryLanguages}
            />
          )}
        />
        <Controller
          name="descriptions"
          control={control}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              inputType={InputTranslatorType.WYSIWYG}
              label={t('internal-analysts:categories_example_item_description')}
              dialogTitle={t('internal-analysts:categories_example_item_description')}
              selectedLanguages={categoryLanguages}
              wysiwygConfig={exampleEditorConfig}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
